import { useState, useEffect, useRef } from 'react';
import { BASE_URL } from '@/config/constants';
import axios from 'axios';
import { ArrowLeft, Mail, KeyRound, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

interface ForgotPasswordProps {
  onBack: () => void;
}

type Step = 'email' | 'code' | 'password' | 'success';

export default function ForgotPassword({ onBack }: ForgotPasswordProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  // Auto email-existence check (debounced, same pattern as login/signup).
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'registered' | 'unregistered'>('idle');
  const emailCheckSeq = useRef(0);
  // Send cooldown (live countdown) + sends remaining, from the backend 429.
  const [cooldownSec, setCooldownSec] = useState(0);
  const [sendsRemaining, setSendsRemaining] = useState<number | null>(null);

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim().toLowerCase());

  // Live countdown for the send cooldown.
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = window.setTimeout(() => setCooldownSec((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldownSec]);

  const formatCooldown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  // Debounced email-existence check as the user types (mirrors login/signup).
  useEffect(() => {
    const normalized = email.trim().toLowerCase();
    const seq = ++emailCheckSeq.current;

    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setEmailStatus('idle');
      return;
    }

    setEmailStatus('checking');
    const timer = window.setTimeout(async () => {
      try {
        const res = await axios.post(`${BASE_URL}/api/auth/check-email`, { email: normalized });
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus(res.data?.available ? 'unregistered' : 'registered');
      } catch {
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus('idle');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [email]);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const response = await axios.post(`${BASE_URL}/api/password-reset/forgot-password`, {
        email: normalizedEmail
      });

      if (response.data.success) {
        setStep('code');
      }
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      // Dedupe: if the auto-check already shows "not registered", don't also
      // pop a separate error box for the same 404.
      if (status === 404 && emailStatus === 'unregistered') {
        setError('');
      } else {
        setError(data.message || 'Failed to send verification code. Please try again.');
      }
      // Capture cooldown + sends remaining from a 429.
      if (status === 429) {
        if (typeof data.retryAfterSec === 'number') setCooldownSec(data.retryAfterSec);
        if (typeof data.sendsRemaining === 'number') setSendsRemaining(data.sendsRemaining);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BASE_URL}/api/password-reset/verify-code`, {
        email: email.trim().toLowerCase(),
        code: code.trim()
      });

      if (response.data.success) {
        setStep('password');
        setAttemptsLeft(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code');
      if (err.response?.data?.attemptsLeft !== undefined) {
        setAttemptsLeft(err.response.data.attemptsLeft);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/api/password-reset/reset-password`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        newPassword
      });

      if (response.data.success) {
        setStep('success');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepNumber = () => {
    switch (step) {
      case 'email': return 1;
      case 'code': return 2;
      case 'password': return 3;
      case 'success': return 4;
    }
  };

  const StepIndicator = ({ stepNum, label, icon: Icon }: { stepNum: number; label: string; icon: any }) => {
    const isActive = getStepNumber() >= stepNum;
    const isCurrent = getStepNumber() === stepNum;
    
    return (
      <div className="flex flex-col items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
            isActive
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground'
          } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <span className={`mt-2 text-xs font-medium transition-colors ${
          isActive ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div className="w-full space-y-8">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login
      </button>

      {/* Progress Steps */}
      {step !== 'success' && (
        <div className="relative">
          <div className="flex justify-between items-start">
            <StepIndicator stepNum={1} label="Email" icon={Mail} />
            <StepIndicator stepNum={2} label="Verify" icon={KeyRound} />
            <StepIndicator stepNum={3} label="Reset" icon={Lock} />
          </div>
          {/* Progress Line */}
          <div className="absolute top-5 left-[20%] right-[20%] h-0.5 bg-muted -z-10">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${((getStepNumber() - 1) / 2) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="mt-8">
        {/* Step 1: Email */}
        {step === 'email' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground">Forgot your password?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                No worries. Enter your email and we'll send you a verification code.
              </p>
            </div>

            <form onSubmit={handleRequestOTP} className="space-y-5">
              {error && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setCooldownSec(0);
                    setSendsRemaining(null);
                  }}
                  className="px-4 py-3"
                  placeholder="Enter your email"
                  autoFocus
                />
                {emailStatus === 'checking' && (
                  <p className="mt-2 text-sm text-muted-foreground">Checking email…</p>
                )}
                {emailStatus === 'unregistered' && (
                  <p className="mt-2 text-sm text-red-500">
                    No account found with this email address
                  </p>
                )}
                {sendsRemaining !== null && sendsRemaining >= 0 && emailStatus === 'registered' && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {sendsRemaining} send{sendsRemaining === 1 ? '' : 's'} left before a 60-minute cooldown
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!isValidEmail(email) || emailStatus === 'unregistered' || cooldownSec > 0}
                loading={loading}
                loadingText="Sending code..."
                size="lg-full"
              >
                {cooldownSec > 0
                  ? `Try again in ${formatCooldown(cooldownSec)}`
                  : 'Send verification code'}
              </Button>
            </form>
          </div>
        )}

        {/* Step 2: Code Verification */}
        {step === 'code' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground">Check your email</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                We've sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
              </p>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-5">
              {error && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  {error}
                  {attemptsLeft !== null && attemptsLeft > 0 && (
                    <span className="block mt-1 text-xs">
                      {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining
                    </span>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="code" className="block text-sm font-semibold text-foreground mb-2">
                  Verification code
                </label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="px-4 py-4 text-2xl font-mono tracking-[0.5em] text-center"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Code expires in 10 minutes
                </p>
              </div>

              <Button
                type="submit"
                disabled={code.length !== 6}
                loading={loading}
                loadingText="Verifying..."
                size="lg-full"
              >
                Verify code
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setAttemptsLeft(null);
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Didn't receive the code? <span className="text-primary font-medium">Resend</span>
              </button>
            </form>
          </div>
        )}

        {/* Step 3: New Password */}
        {step === 'password' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground">Create new password</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Your new password must be at least 8 characters long.
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-5">
              {error && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-sm font-semibold text-foreground mb-2">
                  New password
                </label>
                <PasswordInput
                  id="newPassword"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  minLength={8}
                  showStrengthMeter 
    showCopyBtn
    showRequirements
                  autoFocus
                />

              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-foreground mb-2">
                  Confirm password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  minLength={8}
                />
              </div>

              <Button
                type="submit"
                disabled={!newPassword || !confirmPassword}
                loading={loading}
                loadingText="Resetting password..."
                size="lg-full"
              >
                Reset password
              </Button>
            </form>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <div className="text-center space-y-6 py-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>

            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground">Password reset complete</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
            </div>

            <Button
              onClick={onBack}
              size="lg-full"
            >
              Sign in to your account
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
